import { zh } from '../i18n';
import { formatDurationSeconds, formatInt, formatUsd } from './format';

/**
 * The per-turn cost line (SPEC §10 "cost line with 訂閱額度 honesty").
 *
 * The honesty rule is one-directional and absolute: when the SDK reported no
 * dollars — `costAvailable:false`, which is what an `oauth_token` credential
 * always produces (CONTRACTS §4.1) — the line says 「訂閱額度」 and **no dollar
 * figure is rendered at all**. A `$0.00` there would be a fabricated number
 * describing a real, non-zero spend against a subscription plan.
 *
 * `costUsd === null` is treated the same way even if the flag says otherwise:
 * there is nothing to render, so nothing is invented.
 */
export interface TurnCostInput {
  readonly costUsd: number | null;
  readonly costAvailable: boolean;
  readonly numTurns: number;
  readonly inputTokens: number;
  readonly outputTokens: number;
  readonly cacheReadTokens?: number;
  readonly durationMs?: number | null;
}

export function hasDollars(input: Pick<TurnCostInput, 'costAvailable' | 'costUsd'>): boolean {
  return input.costAvailable && typeof input.costUsd === 'number';
}

/** Ordered parts; the view joins them with a middot so each stays selectable. */
export function costLineParts(input: TurnCostInput): string[] {
  const parts: string[] = [];
  parts.push(hasDollars(input) ? formatUsd(input.costUsd as number) : zh.cost.subscription);
  parts.push(zh.cost.steps(input.numTurns));
  if (typeof input.durationMs === 'number' && input.durationMs > 0) {
    parts.push(zh.cost.seconds(formatDurationSeconds(input.durationMs)));
  }
  parts.push(zh.cost.tokens(formatInt(input.inputTokens), formatInt(input.outputTokens)));
  if (typeof input.cacheReadTokens === 'number' && input.cacheReadTokens > 0) {
    parts.push(zh.cost.cacheRead(formatInt(input.cacheReadTokens)));
  }
  return parts;
}

export function costLineText(input: TurnCostInput): string {
  return costLineParts(input).join(' · ');
}

/**
 * Thread/day totals. `budgetsEnforced:false` ⇒ the meter is replaced by
 * 「訂閱額度制」 rather than a $ figure (SPEC §11, CONTRACTS §4.2).
 */
export function formatAggregateCost(totalUsd: number, budgetsEnforced: boolean): string {
  return budgetsEnforced ? formatUsd(totalUsd) : zh.cost.subscriptionMeter;
}
